import { useState, useEffect } from 'react';

import {
  Text,
  Portal,
  Dialog,
  InputGroup,
  Field,
  Input,
  Flex,
  Button,
  Box,
  Icon,
} from '@chakra-ui/react';

import { FaEye, FaEyeSlash } from 'react-icons/fa';

import { SelectField } from '../common';

import { CONSTANTS } from './CONSTANTS';

export const EditUserDialog = ({
  isOpen,
  setIsOpen,
  onClose,
  onSubmit,
  initialValues = {
    username: '',
    password: '',
    role: '',
    displayName: '',
  },
  isRoleDisabled = false,
  isEdit = false,
}) => {
  const [form, setForm] = useState(initialValues);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setForm(initialValues);
  }, [initialValues, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getUsernameError = (value) => (!value ? CONSTANTS.RequiredField : '');
  const getPasswordError = (value) => (!value ? CONSTANTS.RequiredField : '');
  const getDisplayNameError = (value) =>
    !value ? CONSTANTS.RequiredField : '';
  const getRoleError = (value) => (!value ? CONSTANTS.RequiredField : '');

  const isFormInvalid =
    !!getUsernameError(form.username) ||
    !!getPasswordError(form.password) ||
    !!getDisplayNameError(form.displayName) ||
    (!isRoleDisabled && !!getRoleError(form.role));

  const handleSubmit = () => {
    if (!isFormInvalid) {
      onSubmit(form);
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={setIsOpen}
      closeOnEscape
      closeOnInteractOutside
      trapFocus
      placement='center'
      motionPreset='slide-in-top'>
      <Portal>
        <Dialog.Backdrop backdropFilter='blur(8px)' />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Body mt='4'>
              <Button
                position='absolute'
                top={2}
                right={2}
                size='sm'
                variant='ghost'
                onClick={onClose}>
                {CONSTANTS.Close_Button_value}
              </Button>
              <Text fontWeight='bold' fontSize='lg' mb={6}>
                {isEdit ? CONSTANTS.EditUser : CONSTANTS.CreateUser}
              </Text>
              <Flex direction='column' gap={4}>
                <Field.Root required>
                  <Field.Label>{CONSTANTS.User}</Field.Label>
                  <Input
                    name='username'
                    value={form.username}
                    onChange={handleChange}
                    placeholder={CONSTANTS.User}
                    borderColor={
                      getUsernameError(form.username) ? 'red.500' : undefined
                    }
                  />
                  {getUsernameError(form.username) && (
                    <Text color='red.500' fontSize='12px'>
                      {getUsernameError(form.username)}
                    </Text>
                  )}
                </Field.Root>
                <Field.Root required>
                  <Field.Label>{CONSTANTS.Passwrod}</Field.Label>
                  <InputGroup>
                    <>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name='password'
                        value={form.password}
                        onChange={handleChange}
                        placeholder={CONSTANTS.Passwrod}
                        borderColor={
                          getPasswordError(form.password)
                            ? 'red.500'
                            : undefined
                        }
                      />
                      <Icon
                        as={showPassword ? FaEye : FaEyeSlash}
                        position='absolute'
                        top='50%'
                        right={2}
                        transform='translateY(-50%)'
                        size='sm'
                        color='gray.500'
                        cursor='pointer'
                        onClick={() => setShowPassword((prev) => !prev)}
                      />
                    </>
                  </InputGroup>
                  {getPasswordError(form.password) && (
                    <Text color='red.500' fontSize='sm'>
                      {getPasswordError(form.password)}
                    </Text>
                  )}
                </Field.Root>

                {!isRoleDisabled && (
                  <Field.Root required>
                    <Field.Label>{CONSTANTS.Role}</Field.Label>
                    <SelectField
                      value={
                        CONSTANTS.SELECTION_OPTIONS.items.find(
                          (item) => item.value === form.role
                        )?.label || ''
                      }
                      collection={CONSTANTS.SELECTION_OPTIONS}
                      isLabelVisible={false}
                      onChange={(label) => {
                        const found = CONSTANTS.SELECTION_OPTIONS.items.find(
                          (item) => item.label === label
                        );
                        setForm((prev) => ({
                          ...prev,
                          role: found ? found.value : '',
                        }));
                      }}
                      isDisabled={!CONSTANTS.SELECTION_OPTIONS.items.length}
                    />
                    {getRoleError(form.role) && (
                      <Text color='red.500' fontSize='12px'>
                        {getRoleError(form.role)}
                      </Text>
                    )}
                  </Field.Root>
                )}

                <Field.Root required>
                  <Field.Label>{CONSTANTS.UserName}</Field.Label>
                  <Input
                    name='displayName'
                    value={form.displayName}
                    onChange={handleChange}
                    placeholder={CONSTANTS.UserName}
                    borderColor={
                      getDisplayNameError(form.displayName)
                        ? 'red.500'
                        : undefined
                    }
                  />
                  {getDisplayNameError(form.displayName) && (
                    <Text color='red.500' fontSize='12px'>
                      {getDisplayNameError(form.displayName)}
                    </Text>
                  )}
                </Field.Root>
              </Flex>

              <Box minW={{ base: '100%', md: '128px' }} mt={8}>
                <Button
                  onClick={handleSubmit}
                  fontWeight='bold'
                  fontSize='md'
                  w='full'
                  border='1px solid'
                  borderColor='gray.400'
                  boxShadow='md'
                  _hover={{ bg: 'gray.100', color: 'gray.900' }}
                  disabled={isFormInvalid}>
                  {isEdit ? CONSTANTS.Save : CONSTANTS.Create}
                </Button>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
